import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import PlaylistPlayOutlined from '@mui/icons-material/PlaylistPlayOutlined';
import Send from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { PrimaryButton } from 'components/forms/atoms/FormButtons';
import type { FC } from 'react';
import { NAVY, RADIUS_ITEM } from 'styles/tokens';

import type { PlaygroundMode } from '../types';
import ModelPicker from './ModelPicker';
import ToolChoiceSelect from './ToolChoiceSelect';

interface ConfigBarProps {
  mode: PlaygroundMode;
  model: string;
  onModelChange: (model: string) => void;
  toolChoice: string;
  onToolChoiceChange: (toolChoice: string) => void;
  functionNames: string[];
  onRunSingle: () => void;
  runningSingle: boolean;
  canRun: boolean;
  onOpenAnalysis: () => void;
  onCreateEval: () => void;
  canCreateEval: boolean;
  onRunEvals: () => void;
}

const labelSx = {
  fontFamily: 'Jost, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: alpha(NAVY, 0.5),
};

const secondaryButtonSx = {
  textTransform: 'none' as const,
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: 13,
  color: NAVY,
  borderRadius: RADIUS_ITEM,
  '&:hover': { bgcolor: alpha(NAVY, 0.05) },
  '&.Mui-disabled': { color: alpha(NAVY, 0.3) },
};

const ConfigBar: FC<ConfigBarProps> = ({
  mode,
  model,
  onModelChange,
  toolChoice,
  onToolChoiceChange,
  functionNames,
  onRunSingle,
  runningSingle,
  canRun,
  onOpenAnalysis,
  onCreateEval,
  canCreateEval,
  onRunEvals,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
    {mode === 'single' && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={labelSx}>Modelo</Typography>
        <ModelPicker value={model} onChange={onModelChange} />
      </Box>
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography sx={labelSx}>Tool choice</Typography>
      <ToolChoiceSelect value={toolChoice} onChange={onToolChoiceChange} functionNames={functionNames} />
    </Box>

    <Box sx={{ flex: 1 }} />

    <Button startIcon={<InsightsOutlined sx={{ fontSize: 18 }} />} onClick={onOpenAnalysis} sx={secondaryButtonSx}>
      Analizar prompt
    </Button>

    <Button
      startIcon={<FactCheckOutlined sx={{ fontSize: 18 }} />}
      onClick={onCreateEval}
      disabled={!canCreateEval}
      sx={secondaryButtonSx}
    >
      Crear eval
    </Button>

    <Button startIcon={<PlaylistPlayOutlined sx={{ fontSize: 18 }} />} onClick={onRunEvals} sx={secondaryButtonSx}>
      Correr evals
    </Button>

    {mode === 'single' && (
      <PrimaryButton
        onClick={onRunSingle}
        disabled={!canRun || runningSingle}
        startIcon={runningSingle ? <CircularProgress size={16} sx={{ color: NAVY }} /> : <Send sx={{ fontSize: 17 }} />}
      >
        Ejecutar
      </PrimaryButton>
    )}
  </Box>
);

export default ConfigBar;
